import React, { useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { Search, MapPin, Loader2, X } from "lucide-react";

/**
 * AirportInput
 * Typeahead for airports — search by ICAO / IATA / city / name.
 * Value is an ICAO string. Displays "KOPF · Miami-Opa Locka" once selected.
 */
export default function AirportInput({ value, onChange, placeholder, testId, autoFocus }) {
  const [query, setQuery] = useState("");
  const [meta, setMeta] = useState(null);   // {icao, name, city, country}
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);
  const reqIdRef = useRef(0);   // monotonic request sequence — prevents stale responses overwriting fresh results

  // Resolve meta when the parent supplies an ICAO
  useEffect(() => {
    if (!value) { setMeta(null); return; }
    if (meta?.icao === value.toUpperCase()) return;
    api.get(`/airports/${encodeURIComponent(value)}`).then((r) => setMeta(r.data)).catch(() => setMeta({ icao: value.toUpperCase() }));
  }, [value]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (query.length < 2) { setResults([]); return; }
    clearTimeout(debounceRef.current);
    setBusy(true);
    debounceRef.current = setTimeout(async () => {
      const myReqId = ++reqIdRef.current;
      try {
        const r = await api.get(`/airports/search`, { params: { q: query, limit: 8 } });
        // Only apply the result if this is still the latest in-flight request.
        if (myReqId === reqIdRef.current) {
          setResults(r.data.results || []);
        }
      } catch {
        if (myReqId === reqIdRef.current) setResults([]);
      }
      if (myReqId === reqIdRef.current) setBusy(false);
    }, 120);
    return () => clearTimeout(debounceRef.current);
  }, [query, open]);

  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const pick = (a) => {
    // Order matters: set local meta BEFORE the parent value change so the effect
    // sees meta.icao === value on the next render and skips the refetch.
    setMeta(a);
    setResults([]);
    setQuery("");
    setOpen(false);
    onChange(a.icao);
  };

  const clear = () => {
    onChange("");
    setMeta(null);
    setQuery("");
  };

  return (
    <div ref={wrapRef} className="relative">
      {meta && !open ? (
        <button
          type="button"
          data-testid={testId ? `${testId}-selected` : undefined}
          onClick={() => { setOpen(true); setQuery(""); }}
          className="input flex items-center justify-between text-left w-full group"
        >
          <div>
            <div className="mono text-sm text-cyan font-bold tracking-wider">{meta.icao}{meta.iata ? ` · ${meta.iata}` : ""}</div>
            <div className="text-[11px] text-white/60 mt-0.5 flex items-center gap-1"><MapPin className="h-3 w-3" />{meta.name} · {meta.city}{meta.country ? `, ${meta.country}` : ""}</div>
          </div>
          <span onClick={(e) => { e.stopPropagation(); clear(); }} className="ml-2 text-white/40 hover:text-white p-1"><X className="h-3 w-3" /></span>
        </button>
      ) : (
        <div className="relative">
          <input
            data-testid={testId}
            className="input mono pr-9"
            autoFocus={autoFocus}
            value={query}
            placeholder={placeholder || "Type city or ICAO (e.g. Miami, KTEB)"}
            onFocus={() => setOpen(true)}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin text-cyan" /> : <Search className="h-3.5 w-3.5 text-white/40" />}
          </div>
        </div>
      )}

      {open && (query.length >= 2 || results.length > 0) && (
        <div className="absolute z-50 mt-1 w-full bg-obsidian border border-cyan/30 rounded-lg overflow-hidden shadow-xl" data-testid={testId ? `${testId}-results` : undefined}>
          {busy && results.length === 0 && (
            <div className="px-3 py-3 text-xs text-white/60 mono flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-cyan" /> Searching airports…
            </div>
          )}
          {!busy && results.length === 0 && (
            <div className="px-3 py-3 text-xs text-white/50 mono">No airports match "{query}"</div>
          )}
          {results.map((a) => (
            <button
              key={a.icao}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pick(a); }}
              data-testid={testId ? `${testId}-opt-${a.icao}` : undefined}
              className="block w-full text-left px-3 py-2 hover:bg-cyan/10 border-b border-white/[0.04] last:border-b-0"
            >
              <div className="flex items-center justify-between">
                <div>
                  <span className="mono text-sm font-bold text-cyan">{a.icao}</span>
                  {a.iata && <span className="ml-2 mono text-[11px] text-white/60">{a.iata}</span>}
                </div>
                <span className="mono text-[10px] text-white/40">{a.country}</span>
              </div>
              <div className="text-[11px] text-white/60 mt-0.5">{a.name} · {a.city}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
